import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog";
import Button from "../../form/button";
import { IExpenseTypeRecord } from "../../../lib/types";
import pb from "../../../lib/pocketbase";
import { cn } from "../../../lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NewCategory from "./NewCategory";
import ExpenseCategoryIcon from "./ExpenseCategoryIcon";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import EditCategory from "./editCategory";

export default function ExpenseSettings() {
    const [open, setOpen] = useState(false);
    const [types, setTypes] = useState<IExpenseTypeRecord[]>();

    async function loadTypes() {
        const t: IExpenseTypeRecord[] = await pb.collection("expenseType").getFullList({
            sort: "name"
        });
        setTypes(t);
    }

    async function changeDefault(to: IExpenseTypeRecord) {
        const oldDefault = types?.find((t) => t.default);
        if (oldDefault) {
            await pb.collection("expenseType").update(oldDefault.id, {
                default: false
            });
        }
        await pb.collection("expenseType").update(to.id, {
            default: true
        });
        loadTypes();
    }

    useEffect(() => {
        loadTypes();
    }, []);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant={"ghost"} size={"ghost"}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-6 w-6">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                </Button>
            </DialogTrigger>
            <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="mt-8 md:mt-0">
                <DialogHeader>
                    <DialogTitle>Settings</DialogTitle>
                </DialogHeader>
                <div className="mt-4 flex items-center justify-between gap-2">
                    <h2 className="text-xl font-medium">Categories:</h2>
                    <NewCategory reload={() => loadTypes()} />
                </div>
                <div className="scrollbar flex max-h-80 flex-col gap-2 overflow-y-scroll">
                    {types?.map((type) => (
                        <div className="flex items-center justify-between rounded-lg border px-4 py-2">
                            <div className="flex items-center  gap-4">
                                <ExpenseCategoryIcon category={type} />
                                {type.name}
                            </div>
                            <div className="flex items-center gap-2">
                                {type.default && <h4 className="text-xs italic text-slate-500">(default)</h4>}
                                <Popover>
                                    <PopoverTrigger className="">
                                        <Button variant={"outline"} size={"box"} className="">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                                            </svg>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="end" className="w-48 overflow-hidden rounded-lg border bg-white dark:bg-slate-900">
                                        <div className="flex flex-col gap-4">
                                            <EditCategory category={type} reload={() => loadTypes()}>
                                                <button className="flex cursor-pointer items-center gap-3 hover:opacity-80">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                                    </svg>
                                                    Edit
                                                </button>
                                            </EditCategory>
                                            <button onClick={() => changeDefault(type)} className="flex cursor-pointer items-center gap-3 hover:opacity-80">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
                                                </svg>
                                                Set as default
                                            </button>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
